import Link from "next/link";
import { ArrowRight } from "lucide-react";

interface CTASectionProps {
 title: string;
 description?: string;
 primaryCta: { label: string; href: string };
 secondaryCta?: { label: string; href: string };
 variant?: "gradient" | "dark" | "light";
}

export function CTASection({
 title,
 description,
 primaryCta,
 secondaryCta,
 variant = "gradient",
}: CTASectionProps) {
 if (variant === "dark") {
 return (
 <section className="py-16 lg:py-20 bg-gray-950 relative overflow-hidden">
 <div className="absolute inset-0 pointer-events-none">
 <div className="absolute inset-0 cta-aurora-sweep opacity-70" />
 <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-blue-600/16 blur-[110px] cta-float-slow" />
 <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-violet-600/14 blur-[110px] cta-float-reverse" />
 </div>
 <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
 <h2 className="text-3xl lg:text-4xl font-bold text-white">{title}</h2>
 {description && <p className="mt-4 text-lg text-gray-400">{description}</p>}
 <div className="mt-8 flex justify-center gap-4 flex-wrap">
 <Link href={primaryCta.href} className="inline-flex items-center rounded-xl bg-gradient-to-r from-cyan-500 via-blue-600 to-fuchsia-600 bg-[length:220%_220%] px-6 py-3 font-semibold text-white shadow-lg shadow-blue-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-blue-600/45 cta-aurora-button">
 {primaryCta.label} <ArrowRight className="w-4 h-4 ml-2" />
 </Link>
 {secondaryCta && (
 <Link href={secondaryCta.href} className="cta-secondary-button inline-flex items-center rounded-xl px-6 py-3 font-semibold transition-all duration-300">
 {secondaryCta.label}
 </Link>
 )}
 </div>
 </div>
 </section>
 );
 }

 if (variant === "light") {
 return (
 <section className="py-12">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="relative overflow-hidden rounded-2xl border border-border bg-muted p-8 text-center lg:p-12">
 <div className="absolute inset-0 pointer-events-none">
 <div className="absolute inset-0 cta-light-sweep" />
 <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-primary/10 blur-[60px] cta-float-slow" />
 <div className="absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-violet-500/10 blur-[60px] cta-float-reverse" />
 </div>
 <div className="relative">
 <h2 className="text-2xl lg:text-3xl font-bold text-foreground">{title}</h2>
 {description && <p className="mt-3 text-muted-foreground">{description}</p>}
 <div className="mt-6 flex justify-center gap-4 flex-wrap">
 <Link href={primaryCta.href} className="inline-flex items-center rounded-xl bg-gradient-to-r from-cyan-500 via-blue-600 to-fuchsia-600 bg-[length:220%_220%] px-6 py-3 font-semibold text-white shadow-lg shadow-blue-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-blue-600/45 cta-aurora-button">
 {primaryCta.label} <ArrowRight className="w-4 h-4 ml-2" />
 </Link>
 {secondaryCta && (
 <Link href={secondaryCta.href} className="cta-secondary-button inline-flex items-center rounded-xl px-6 py-3 font-semibold transition-all duration-300">
 {secondaryCta.label}
 </Link>
 )}
 </div>
 </div>
 </div>
 </div>
 </section>
 );
 }

 // gradient variant (default)
 return (
 <section className="py-12">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="service-accent-gradient relative overflow-hidden rounded-2xl p-8 text-center text-white lg:p-12">
 <div className="absolute inset-0 pointer-events-none">
 <div className="absolute inset-0 cta-aurora-sweep opacity-65" />
 <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-white/10 blur-[70px] cta-float-slow" />
 <div className="absolute -bottom-20 -left-20 h-60 w-60 rounded-full bg-white/8 blur-[70px] cta-float-reverse" />
 </div>
 <div className="relative">
 <h2 className="text-2xl lg:text-3xl font-bold">{title}</h2>
 {description && <p className="mt-3 text-white/80">{description}</p>}
 <div className="mt-6 flex justify-center gap-4 flex-wrap">
 <Link href={primaryCta.href} className="cta-aurora-button inline-flex items-center rounded-xl bg-gradient-to-r from-cyan-500 via-blue-600 to-fuchsia-600 bg-[length:220%_220%] px-6 py-3 font-semibold text-white shadow-lg shadow-blue-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-blue-600/45">
 {primaryCta.label} <ArrowRight className="w-4 h-4 ml-2" />
 </Link>
 {secondaryCta && (
 <Link href={secondaryCta.href} className="cta-secondary-button inline-flex items-center rounded-xl px-6 py-3 font-semibold transition-all duration-300">
 {secondaryCta.label}
 </Link>
 )}
 </div>
 </div>
 </div>
 </div>
 </section>
 );
}
